import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  Target, 
  Users, 
  Trophy, 
  Zap, 
  Heart, 
  Shield, 
  Linkedin,
  Mail,
  MapPin,
  Calendar,
  TrendingUp,
  Award
} from 'lucide-react';
import { gameStats } from '../data/mockData';

const About = () => {
  const navigate = useNavigate();
  const teamMembers = [
    {
      id: 1,
      name: "Rahul Sharma",
      role: "CEO & Co-Founder", 
      bio: "Former Product Manager at Paytm Games with 8+ years in fintech and gaming.",
      linkedin: "#",
      image: "/team/rahul.jpg"
    },
    {
      id: 2,
      name: "Priya Patel",
      role: "CTO & Co-Founder",
      bio: "Ex-Google engineer specializing in scalable gaming platforms and mobile architecture.",
      linkedin: "#", 
      image: "/team/priya.jpg"
    },
    {
      id: 3,
      name: "Arjun Singh",
      role: "Head of Product",
      bio: "Gaming industry veteran with successful launches at Dream11 and Mobile Premier League.",
      linkedin: "#",
      image: "/team/arjun.jpg"
    },
    {
      id: 4,
      name: "Sneha Reddy",
      role: "Head of Growth",
      bio: "Marketing expert with proven track record in user acquisition and retention strategies.",
      linkedin: "#",
      image: "/team/sneha.jpg"
    }
  ];

  const milestones = [
    {
      year: "2023",
      quarter: "Q1",
      title: "Company Founded",
      description: "Started with the vision to revolutionize skill-based mobile gaming in India."
    },
    {
      year: "2023", 
      quarter: "Q2",
      title: "MVP Development",
      description: "Built and tested initial platform with focus groups in Mumbai and Bangalore."
    },
    {
      year: "2023",
      quarter: "Q4", 
      title: "Beta Launch",
      description: "Launched beta version with 1,000 early users and first prize distributions."
    },
    {
      year: "2024",
      quarter: "Q1",
      title: "Public Launch",
      description: "Official launch with 10,000+ users and partnerships with major gadget brands."
    },
    {
      year: "2024",
      quarter: "Q3",
      title: "50K+ Players",
      description: "Reached significant user milestone with ₹2Cr+ in prizes distributed."
    },
    {
      year: "2025",
      quarter: "Q1", 
      title: "Series A Funding",
      description: "Secured Series A funding to expand across India and launch new game categories."
    }
  ];

  const values = [
    {
      icon: Shield,
      title: "Fairness First",
      description: "Every game is transparent, skill-based, and uses advanced anti-cheat technology."
    },
    {
      icon: Heart,
      title: "Player-Centric",
      description: "We prioritize player experience, satisfaction, and responsible gaming practices."
    },
    {
      icon: Zap,
      title: "Innovation",
      description: "Constantly evolving with new game formats, features, and exciting prize categories."
    },
    {
      icon: Target,
      title: "Excellence",
      description: "Committed to delivering the highest quality gaming experience and customer service."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black pt-20">
      {/* Hero Section */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/10 via-purple-900/5 to-transparent"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto animate-fade-in-up">
            <Badge className="bg-yellow-400/20 text-yellow-400 border-yellow-400/30 mb-6 px-6 py-2">
              Our Story
            </Badge>
            
            <h1 className="font-heading text-4xl lg:text-6xl font-bold text-white mb-6">
              About 
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-400">
                {' '}Fokatmain
              </span>
            </h1>
            
            <p className="text-xl lg:text-2xl text-gray-300 mb-12 leading-relaxed">
              We're building India's most exciting skill-based gaming platform, 
              where players compete fairly and win amazing gadgets through pure skill.
            </p>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-gradient-to-b from-gray-900/50 to-transparent">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Mission */}
            <div className="animate-slide-in-left">
              <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30 mb-6">
                Our Mission
              </Badge>
              
              <h2 className="font-heading text-3xl lg:text-4xl font-bold text-white mb-6">
                Making Competitive Gaming Accessible & Rewarding
              </h2>
              
              <div className="space-y-6 text-lg text-gray-300">
                <p>
                  Our mission is to create the most fair, transparent, and rewarding skill-based 
                  gaming platform in India. We believe that gaming should reward talent, not luck.
                </p>
                
                <p>
                  By offering quick, engaging games with real gadget prizes, we're building a 
                  community where players can showcase their skills and win amazing rewards 
                  they actually want to use.
                </p>
              </div>
              
              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <Button 
                  className="btn-primary px-6 py-3"
                  onClick={() => navigate('/signup')}
                >
                  Join Our Mission
                </Button>
                <Button 
                  className="btn-secondary px-6 py-3"
                  onClick={() => navigate('/contact')}
                >
                  Contact Us
                </Button>
              </div>
            </div>
            
            {/* Stats */}
            <div className="animate-slide-in-right">
              <div className="grid grid-cols-2 gap-6">
                {[
                  { label: 'Total Players', value: gameStats.totalPlayers, icon: Users },
                  { label: 'Games Played', value: gameStats.gamesPlayed, icon: Trophy },
                  { label: 'Prizes Distributed', value: gameStats.totalPrizeValue, icon: Award },
                  { label: 'Happy Winners', value: gameStats.prizesWon, icon: Heart }
                ].map((stat, index) => {
                  const IconComponent = stat.icon;
                  return (
                    <Card key={index} className="gaming-card text-center p-6">
                      <CardHeader className="pb-2">
                        <IconComponent className="w-8 h-8 text-yellow-400 mx-auto mb-2" />
                        <CardTitle className="text-2xl lg:text-3xl font-bold text-white">
                          {stat.value}
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-gray-400 text-sm">{stat.label}</p>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-gradient-to-b from-transparent to-gray-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30 mb-6">
              Our Values
            </Badge>
            <h2 className="font-heading text-3xl lg:text-4xl font-bold text-white mb-6">
              What Drives Us
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Our core values shape everything we do, from product development to customer service
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const IconComponent = value.icon;
              return (
                <Card key={index} className="gaming-card text-center p-6 animate-fade-in-up" style={{ animationDelay: `${index * 0.1}s` }}>
                  <CardHeader className="pb-4">
                    <div className="mx-auto mb-4 p-4 bg-yellow-400/20 rounded-full w-16 h-16 flex items-center justify-center">
                      <IconComponent className="w-8 h-8 text-yellow-400" />
                    </div>
                    <CardTitle className="text-xl text-white">{value.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-400 leading-relaxed">{value.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-20 bg-gradient-to-br from-blue-900/20 to-purple-900/20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="animate-fade-in-up">
            <h2 className="font-heading text-3xl lg:text-4xl font-bold text-white mb-6">
              Want to Join Our Journey?
            </h2>
            <p className="text-xl text-gray-400 mb-8 leading-relaxed">
              Whether you're a potential partner, investor, or just want to say hello, we'd love to hear from you.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <Button className="btn-primary px-8 py-4 text-lg font-semibold rounded-full hover:scale-105 transition-all duration-300 flex items-center space-x-3">
                <Mail className="w-5 h-5" />
                <span>Get in Touch</span>
              </Button>
              <Button className="btn-secondary px-8 py-4 text-lg font-semibold rounded-full hover:scale-105 transition-all duration-300 flex items-center space-x-3">
                <Calendar className="w-5 h-5" />
                <span>Schedule a Call</span>
              </Button>
            </div>
            
            <div className="flex justify-center space-x-8 text-sm text-gray-500">
              <span className="flex items-center space-x-2">
                <MapPin className="w-4 h-4" />
                <span>Mumbai, India</span>
              </span>
              <span className="flex items-center space-x-2">
                <TrendingUp className="w-4 h-4" />
                <span>Rapidly Growing</span>
              </span>
              <span className="flex items-center space-x-2">
                <Users className="w-4 h-4" />
                <span>We're Hiring!</span>
              </span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;